import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Box, Button, CircularProgress, Paper, Typography, IconButton, Chip } from '@mui/material';
import { KeyboardArrowDown, ArrowBack } from '@mui/icons-material';
import { offerStandaloneService, type OfferStandalone } from '../services/offerStandaloneService';
import { getBoardUrlForOffer } from '../services/collectifileService';
import RulerIcon from '../components/icons/RulerIcon';
import OfferDetails from '../components/offers/OfferDetails';
import AddOfferStandaloneForm from '../components/offers/AddOfferStandaloneForm';

function computeDisplayId(offer: OfferStandalone): string {
  const anyOffer = offer as any;
  if (anyOffer.offer_id) return anyOffer.offer_id as string;
  if (anyOffer.offer_sequence_number && anyOffer.created_at) {
    const year = new Date(anyOffer.created_at).getFullYear();
    return `OF.${anyOffer.offer_sequence_number}.${year}`;
  }
  return offer.id.substring(0, 8);
}

const OfferDetailPage: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [offer, setOffer] = React.useState<OfferStandalone | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [editOpen, setEditOpen] = React.useState(false);

  const renderStatusChip = (status?: string) => {
    const text = '#020242';
    const map: Record<string, { bg: string; fg: string }> = {
      'w trakcie': { bg: '#E6F0FF', fg: text },
      'najlepsza oferta': { bg: '#EAF7EA', fg: text },
      'druga oferta': { bg: '#FEEED6', fg: text },
      'trzecia i dalej oferta': { bg: '#EFE6FF', fg: text },
      'Uzupełnianie dokumentów': { bg: '#E6F7FF', fg: text },
      'Wygrana': { bg: '#E6F5E6', fg: text },
      'odrzucona': { bg: '#FFE6E6', fg: text },
      'przegrana': { bg: '#FFE6E6', fg: text },
      'Prekwalifikacja': { bg: '#FFF5CC', fg: text },
    };
    const palette = map[status || ''] || { bg: '#F1F1F6', fg: text };
    return (
      <Chip
        label={status || '-'}
        size="small"
        sx={{
          bgcolor: palette.bg,
          color: palette.fg,
          borderRadius: '4px',
          minHeight: 28,
          height: 28,
          '& .MuiChip-label': {
            px: 1,
            py: 0,
            fontFamily: 'Roboto, sans-serif',
            fontSize: '14px',
            fontWeight: 400,
            lineHeight: '16px',
            color: palette.fg
          }
        }}
      />
    );
  };

  React.useEffect(() => {
    let mounted = true;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const all = await offerStandaloneService.getAllOffers();
        const found = all.find(o => computeDisplayId(o) === id || (o as any).offer_id === id || o.id === id);
        if (mounted) setOffer(found || null);
        if (!found && mounted) setError('Nie znaleziono oferty');
      } catch (e: any) {
        if (mounted) setError('Błąd podczas pobierania oferty');
      } finally {
        if (mounted) setLoading(false);
      }
    };
    load();
    return () => { mounted = false; };
  }, [id]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 2 }}>
        <CircularProgress size={24} />
        <Typography>Ładowanie oferty…</Typography>
      </Box>
    );
  }

  if (error || !offer) {
    return (
      <Box sx={{ bgcolor: '#FAFAFA', mt: '20px', position: 'relative', p: 2 }}>
        <Box
          onClick={() => navigate('/offers')}
          sx={{
            position: 'absolute',
            top: '-24px',
            left: 0,
            display: 'inline-flex',
            alignItems: 'center',
            gap: '4px',
            cursor: 'pointer',
            userSelect: 'none'
          }}
          aria-label="Wróć do ofert"
          role="button"
        >
          <KeyboardArrowDown sx={{ transform: 'rotate(90deg)', width: 16, height: 16, color: '#1F1FB0' }} />
          <Typography
            sx={{
              fontFamily: 'Roboto, sans-serif',
              fontSize: '14px',
              lineHeight: '16px',
              fontWeight: 500,
              color: '#1F1FB0'
            }}
          >
            Wróć do ofert
          </Typography>
        </Box>
        <IconButton size="small" onClick={() => navigate(-1)} aria-label="Wstecz" sx={{ mb: 1 }}>
          <ArrowBack fontSize="small" />
        </IconButton>
        <Typography color="error">{error || 'Nie znaleziono oferty'}</Typography>
      </Box>
    );
  }

  return (
    <>
      <Box sx={{ bgcolor: '#FAFAFA', mt: '20px', position: 'relative' }}>
        {/* Wróć do ofert – nad kontenerem, bez przesuwania układu */}
        <Box
          onClick={() => navigate('/offers')}
          sx={{
            position: 'absolute',
            top: '-24px',
            left: 0,
            display: 'inline-flex',
            alignItems: 'center',
            gap: '4px',
            cursor: 'pointer',
            userSelect: 'none'
          }}
          aria-label="Wróć do ofert"
          role="button"
        >
          <KeyboardArrowDown sx={{ transform: 'rotate(90deg)', width: 16, height: 16, color: '#1F1FB0' }} />
          <Typography
            sx={{
              fontFamily: 'Roboto, sans-serif',
              fontSize: '14px',
              lineHeight: '16px',
              fontWeight: 500,
              color: '#1F1FB0'
            }}
          >
            Wróć do ofert
          </Typography>
        </Box>
        {/* Wiersz nagłówka zgodny z pozycją nagłówka na stronie projektów */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 0, mb: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
            <Typography
              component="h1"
              sx={{
                fontFamily: 'Roboto, sans-serif',
                fontSize: '20px',
                lineHeight: '24px',
                fontWeight: 400,
                color: '#020242',
                m: 0
              }}
            >
              Oferta {computeDisplayId(offer)}
            </Typography>
            {renderStatusChip(offer.status)}
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            {/* Ikony: Drive, Strona, Board – po lewej od przycisku, z marginesem 24px */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mr: '24px' }}>
              {/* Google Drive */}
              {offer.google_drive_link ? (
                <IconButton
                  size="small"
                  component="a"
                  href={offer.google_drive_link}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ p: 0.25, width: 32, height: 32 }}
                  aria-label="Dysk Google"
                >
                  <Box component="img" src="/assets/icons/drive.svg" alt="Dysk" />
                </IconButton>
              ) : null}
              {/* Strona zamawiającego */}
              {offer.client_website ? (
                <IconButton
                  size="small"
                  component="a"
                  href={offer.client_website}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ p: 0.25, width: 32, height: 32 }}
                  aria-label="Strona zamawiającego"
                >
                  <Box component="img" src="/assets/icons/planet.svg" alt="Strona" />
                </IconButton>
              ) : null}
              {/* Board */}
              {offer.offer_id ? (
                <IconButton
                  size="small"
                  component="a"
                  href={getBoardUrlForOffer(offer.offer_id, (offer as any).collectifile_board_id, offer.miro_link)}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ p: 0.25, width: 32, height: 32 }}
                  aria-label="Board"
                >
                  <RulerIcon filled={!!(offer as any).collectifile_board_id || !!offer.miro_link} sx={{ width: 24, height: 24 }} />
                </IconButton>
              ) : null}
              {/* CAD (DWG/DXF) */}
              {offer.offer_id ? (
                <IconButton
                  size="small"
                  component="a"
                  href={`/cad/${encodeURIComponent(offer.offer_id)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ p: 0.25, width: 32, height: 32 }}
                  aria-label="CAD"
                >
                  <Box component="img" src="/assets/icons/architecture.svg" alt="CAD" />
                </IconButton>
              ) : null}
            </Box>
            <Button
              variant="contained"
              onClick={() => setEditOpen(true)}
              sx={{
                width: 116,
                height: 40,
                px: 1,
                whiteSpace: 'nowrap',
                borderRadius: '4px',
                textTransform: 'none',
                fontSize: '14px',
                fontWeight: 600,
                backgroundColor: '#4040DB',
                color: '#fff',
                '&:hover': { backgroundColor: '#1F1FB0' },
                '&:active': { backgroundColor: '#1A1A98' }
              }}
            >
              Edytuj ofertę
            </Button>
          </Box>
        </Box>
        <OfferDetails offer={offer} />
        <AddOfferStandaloneForm
          open={editOpen}
          onClose={() => setEditOpen(false)}
          onAdd={(edited: OfferStandalone) => {
            try {
              setOffer(edited);
            } finally {
              setEditOpen(false);
            }
          }}
          isEditMode={true}
          initialData={offer}
        />
      </Box>
    </>
  );
};

export default OfferDetailPage;


